import { CircleDashed, Database, FileCheck, FileClock, FileMinus } from 'lucide-react';
import { Icon } from './Icon';
import { InfoPopover } from '../../components/InfoPopover';
import { denominatorOf, num, pctAxis, sentence, share, type CountMessage } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  noData: {
    id: 'common.ui.doneSplit.noData',
    defaultMessage: 'Нет данных.',
    description: 'Жирное начало пустого состояния полосы «выполнено / нет отчёта»; за ним идёт причина',
  },
  noWorks: {
    id: 'common.ui.doneSplit.noWorks',
    defaultMessage: 'Работы не назначались.',
    description: 'Жирное начало пустого состояния полосы выполнения, когда знаменатель равен нулю',
  },
  nothingToCount: {
    id: 'common.ui.doneSplit.nothingToCount',
    defaultMessage: 'Считать нечего: работ на полях по этому срезу нет.',
    description: 'Пояснение после «Работы не назначались.» — в выбранном срезе нет ни одной работы',
  },
  done: {
    id: 'common.ui.doneSplit.done',
    defaultMessage: 'Выполнено',
    description: 'Подпись оси полосы: работы, по которым внесён отчёт «выполнено»; коротко',
  },
  inProgress: {
    id: 'common.ui.doneSplit.inProgress',
    defaultMessage: 'В процессе',
    description: 'Подпись оси полосы: отчёт есть, работа не закончена; коротко',
  },
  noReport: {
    id: 'common.ui.doneSplit.noReport',
    defaultMessage: 'Нет отчёта',
    description: 'Подпись оси полосы: отчёта не поступало (про данные, а не вывод «не сделано»); коротко',
  },
  reported: {
    id: 'common.ui.doneSplit.reported',
    defaultMessage: 'Отчитались',
    description: 'Подпись оси полосы (когда сервер не разделил «в процессе»): по скольким поступил отчёт; коротко',
  },
  silent: {
    id: 'common.ui.doneSplit.silent',
    defaultMessage: 'Без отчёта',
    description: 'Подпись оси полосы (когда сервер не разделил «в процессе»): остаток без отчёта; коротко',
  },
  axisLabel: {
    id: 'common.ui.doneSplit.axisLabel',
    defaultMessage: '{name}: {value} из {total}',
    description: 'aria-label полосы оси; {name} — подпись оси («Выполнено»), {value}/{total} — отформатированные числа',
  },
  denominatorNote: {
    id: 'common.ui.doneSplit.denominatorNote',
    defaultMessage: 'Знаменатель — {total} {unit}.',
    description: 'Подпись под полосой выполнения; {total} — число, {unit} — слово в нужной форме («работ на полях»)',
  },
  helpLabel: {
    id: 'common.ui.doneSplit.helpLabel',
    defaultMessage: 'Что значит «выполнено» и «в процессе»',
    description: 'aria-label значка «i» под полосой выполнения (открывает пояснение)',
  },
  helpText: {
    id: 'common.ui.doneSplit.helpText',
    defaultMessage:
      '«Выполнено» — по работе на поле внесён отчёт «выполнено». «В процессе» — отчёт есть, но работа не закончена; в «выполнено» не входит. «Нет отчёта» — отчёта не поступало: это про данные, а не вывод «не сделано». Отчёт принимается фактом без проверки; отметка проверяющего — необязательная, поверх.',
    description: 'Пояснение по нажатию на «i» под полосой выполнения: что значат три исхода; три термина в кавычках должны совпасть с подписями осей',
  },
});

export interface DoneCounts {
  /** Знаменатель: сколько всего назначено. */
  total: number;
  /** Ось 1: по скольким поступил отчёт. */
  reported: number;
  /**
   * Отметки проверяющего. Долей «проверено» компонент НЕ рисует: система
   * учётная, отчёт принят как факт, проверка — необязательная отметка поверх
   * (журнал решений №28). Поле оставлено, чтобы вызовы не менялись.
   */
  verified?: number;
  /**
   * Из `reported` — отчёты «в процессе» (работа начата, финального «выполнено»
   * нет; agroai-6al.35, журнал №290). Задано — компонент разводит три исхода:
   * «Выполнено», «В процессе», «Нет отчёта». `null`/не задано — сервер степень
   * не считал, и рисуется прежняя пара «Отчитались / Без отчёта».
   */
  inProgress?: number | null;
}

type Size = 'sm' | 'md';

/** Палитра полосы «Отчитались»: по умолчанию зелёная, `water` — синяя (страница «Вода»). */
export type DoneTone = 'default' | 'water';

/**
 * «Отчитались» и остаток «без отчёта» — над одним знаменателем.
 * Когда счёта нет, `reason` обязателен — отсутствие данных говорит словами.
 */
export type DoneSplitProps =
  | {
      counts: DoneCounts;
      /** Имя знаменателя: «обязательств», «контуров», «га». */
      denominator: string | CountMessage;
      /** Для идущего окна: «ещё не срок» рядом с нулём. */
      windowNote?: string;
      size?: Size;
      tone?: DoneTone;
      /**
       * `short` — только «Знаменатель — N …»: когда несколько осей стоят
       * рядом (окна «Сейчас в поле»), пояснение «выполнено / в процессе»
       * печатается один раз под списком, а не под каждой осью.
       */
      note?: 'full' | 'short';
      reason?: never;
    }
  | {
      counts: null;
      /** Названная причина отсутствия счёта. */
      reason: string;
      denominator: string | CountMessage;
      windowNote?: never;
      size?: Size;
      tone?: DoneTone;
    };

function Axis({
  icon,
  name,
  value,
  total,
  fill,
}: {
  icon: typeof FileCheck;
  name: string;
  value: number;
  total: number;
  fill: 'reported' | 'progress' | 'silent';
}) {
  const t = useT();
  const ratio = share(value, total);
  return (
    <div className="done__axis">
      <div className="done__line">
        <span className="done__name">
          <Icon icon={icon} size={16} />
          {name}
        </span>
        <span className="done__count">
          {num(value)}
          {ratio === null ? '' : ` · ${pctAxis(ratio)}`}
        </span>
      </div>
      <div
        className="done__track"
        role="img"
        aria-label={t(m.axisLabel, { name, value: num(value), total: num(total) })}
      >
        <div
          className={`done__fill done__fill--${fill}`}
          style={{ width: `${ratio === null ? 0 : Math.min(100, ratio)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Единственный способ показать «сделано / не сделано»: отчитались и остаток.
 * Остаток называется «без отчёта»: это про данные, а не про работу в поле.
 */
export function DoneSplit(props: DoneSplitProps) {
  const t = useT();
  const size = props.size ?? 'md';

  if (props.counts === null) {
    return (
      <div className={`done done--${size}`}>
        <div className="done__state">
          <Icon icon={Database} size={16} />
          <span>
            <strong>{t(m.noData)}</strong> {sentence(props.reason)}
          </span>
        </div>
      </div>
    );
  }

  const { total, reported } = props.counts;

  if (total === 0) {
    return (
      <div className={`done done--${size}`}>
        <div className="done__state">
          <Icon icon={CircleDashed} size={16} />
          <span>
            <strong>{t(m.noWorks)}</strong> {t(m.nothingToCount)}
          </span>
        </div>
      </div>
    );
  }

  const silent = Math.max(0, total - reported);
  const inProgress = props.counts.inProgress;
  const split = typeof inProgress === 'number';

  return (
    <div className={`done done--${size}${props.tone === 'water' ? ' done--water' : ''}`}>
      {split ? (
        <>
          <Axis icon={FileCheck} name={t(m.done)} value={Math.max(0, reported - inProgress)} total={total} fill="reported" />
          <Axis icon={FileClock} name={t(m.inProgress)} value={inProgress} total={total} fill="progress" />
          <Axis icon={FileMinus} name={t(m.noReport)} value={silent} total={total} fill="silent" />
        </>
      ) : (
        <>
          <Axis icon={FileCheck} name={t(m.reported)} value={reported} total={total} fill="reported" />
          <Axis icon={FileMinus} name={t(m.silent)} value={silent} total={total} fill="silent" />
        </>
      )}
      {/* На виду — знаменатель и «ещё не срок» рядом с нулём (screen-levels.md:
          это говорится вслух); определение «выполнено / в процессе» — в «i»
          (07.10.2026): оно повторялось абзацем под каждой осью на шести экранах. */}
      <div className="done__note">
        {t(m.denominatorNote, { total: num(total), unit: denominatorOf(total, props.denominator) })}{' '}
        {props.windowNote && props.note !== 'short' ? sentence(props.windowNote) : null}
        {split && props.note !== 'short' ? (
          <InfoPopover icon="info" label={t(m.helpLabel)}>
            {t(m.helpText)}
          </InfoPopover>
        ) : null}
      </div>
    </div>
  );
}
